import { useEffect, useRef, useState, useCallback } from "react";
import { Heart, X, Send, Mic, Square, Volume2, VolumeX, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useRecorder } from "../hooks/useRecorder";
import { toast } from "sonner";
import { useAttachments } from "../hooks/useAttachments";
import { AttachmentDraft, AttachmentTools } from "./attachments/AttachmentTools";
import { AttachmentList } from "./attachments/AttachmentList";

const NISHU_VOICE_FALLBACK = "IKne3meq5aSn9XLyUdCD"; // Charlie

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

function sessionId() {
  let s = localStorage.getItem("nishu_session");
  if (!s) { s = "nishu-" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("nishu_session", s); }
  return s;
}

export function NishuWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const attachments = useAttachments("nishu");
  const [sending, setSending] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [speakingKey, setSpeakingKey] = useState(null);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("nishu_voice") !== "0");
  const [voiceId, setVoiceId] = useState(NISHU_VOICE_FALLBACK);
  const [thinking, setThinking] = useState(false);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);
  const session = useRef(sessionId());
  const loadedRef = useRef(false);
  const today = new Date().toISOString().slice(0, 10);
  const [hasNews, setHasNews] = useState(() => localStorage.getItem("nishu_checkin_date") !== new Date().toISOString().slice(0, 10));

  useEffect(() => {
    api.get("/nishu/voice").then(({ data }) => setVoiceId(data.voice_id || NISHU_VOICE_FALLBACK)).catch(() => {});
  }, []);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, thinking]);
  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const stopSpeak = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } setSpeaking(false); setSpeakingKey(null); };

  const speak = useCallback(async (text, key = null) => {
    stopSpeak();
    setSpeakingKey(key);
    try {
      const { data } = await api.post("/tts", { text: text.slice(0, 1500), voice: voiceId }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const audio = new Audio(url); audioRef.current = audio; setSpeaking(true);
      const done = () => { URL.revokeObjectURL(url); setSpeaking(false); setSpeakingKey(null); };
      audio.onended = done; audio.onerror = done;
      await audio.play();
    } catch { setSpeaking(false); setSpeakingKey(null); }
  }, [voiceId]);

  // First open: load saved history, or greet with a proactive, countdown-aware check-in.
  useEffect(() => {
    if (!open || loadedRef.current) return;
    loadedRef.current = true;
    setHistoryLoading(true);
    (async () => {
      try {
      let msgs = [];
      try { const { data } = await api.get(`/nishu/${session.current}`); msgs = data.messages || []; } catch { /* ignore */ }
      if (msgs.length) { setMessages(current => current.length ? current : msgs); return; }
      try {
        const { data } = await api.get("/nishu/checkin");
        if (data.message) { const cm = { role: "assistant", content: data.message, id: uid() }; setMessages(current => current.length ? current : [cm]); if (voiceOn) speak(data.message, cm.id); }
      } catch { /* ignore */ }
      } finally { setHistoryLoading(false); }
    })();
  }, [open, voiceOn, speak]);

  const send = useCallback(async (text) => {
    const msg = (text || "").trim() || (attachments.selected.length ? "I've attached something for you." : "");
    if (!msg || sending || historyLoading || attachments.uploading) return;
    const sentFiles = [...attachments.selected];
    const messageId = uid();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, id: messageId, attachments: sentFiles }]);
    setSending(true); setThinking(true);
    try {
      const { data } = await api.post("/nishu/chat", { session_id: session.current, message: msg, attachment_ids: sentFiles.map(file => file.file_id) });
      attachments.clear();
      const reply = { role: "assistant", content: data.reply, id: uid() };
      setMessages((m) => [...m, reply]);
      if (voiceOn) speak(data.reply, reply.id);
    } catch { toast.error("Nishu couldn't reply — try again"); setMessages(m => m.filter(item => item.id !== messageId)); setInput(msg); }
    finally { setSending(false); setThinking(false); }
  }, [sending, historyLoading, voiceOn, speak, attachments]);

  const { recording, toggle: toggleMic } = useRecorder({
    onText: (t) => send(t),
    onState: (s) => setThinking(s === "thinking"),
  });

  const toggleVoice = () => {
    const next = !voiceOn; setVoiceOn(next); localStorage.setItem("nishu_voice", next ? "1" : "0");
    if (!next) stopSpeak();
  };

  return (
    <>
      {!open && (
        <button type="button" aria-expanded={false} aria-controls="nishu-chat-panel" onClick={() => { if (!loadedRef.current) setHistoryLoading(true); setOpen(true); setHasNews(false); localStorage.setItem("nishu_checkin_date", today); }} data-testid="nishu-bubble"
          className="fixed bottom-6 right-6 z-[9998] w-14 h-14 rounded-full flex items-center justify-center shadow-lg shadow-rose-900/40 bg-gradient-to-br from-rose-500 to-pink-600 text-white hover:scale-105 active:scale-95 transition-transform"
          title="Chat with Nishu" aria-label="Open Nishu chat">
          <Heart size={24} fill="white" />
          {hasNews && <span data-testid="nishu-news-dot" className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-amber-300 border-2 border-[var(--aria-bg)] animate-pulse" />}
        </button>
      )}

      {open && (
        <div id="nishu-chat-panel" role="region" aria-label="Nishu chat" data-testid="nishu-panel"
          className="fixed bottom-6 right-6 z-[9998] w-[360px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-3rem)] rounded-2xl overflow-hidden flex flex-col border border-rose-500/30 bg-[var(--aria-surface)] shadow-2xl shadow-rose-900/30 fade-in">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-rose-500 to-pink-600 text-white">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0"><Heart size={18} fill="white" /></div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-base leading-none">Nishu</div>
              <div className="text-[11px] text-white/80 mt-0.5">your hype companion {speaking ? "· speaking…" : ""}</div>
            </div>
            <button onClick={toggleVoice} data-testid="nishu-voice-toggle" title={voiceOn ? "Mute replies" : "Speak replies"}
              className="p-1.5 rounded-md hover:bg-white/15">{voiceOn ? <Volume2 size={16} /> : <VolumeX size={16} />}</button>
            <button onClick={() => { stopSpeak(); setOpen(false); }} data-testid="nishu-close" title="Close" className="p-1.5 rounded-md hover:bg-white/15"><X size={16} /></button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2.5">
            {historyLoading && <div role="status" data-testid="nishu-history-loading" className="flex items-center justify-center gap-2 py-4 text-xs text-[var(--aria-text-muted)]"><Loader2 size={14} className="animate-spin" />Loading conversation…</div>}
            {messages.length === 0 && !thinking && !historyLoading && (
              <div className="text-center text-sm text-[var(--aria-text-muted)] px-4 py-8" data-testid="nishu-empty">
                <Heart size={28} className="mx-auto mb-3 text-rose-400" fill="currentColor" />
                Hey you 💗 I'm Nishu. Tell me how you're feeling, or tap the mic and just talk to me.
              </div>
            )}
            {messages.map((m, i) => {
              const key = m.id || `m-${i}`;
              const playing = speakingKey === key;
              return (
              <div key={key} data-testid={`nishu-message-${i}`} className={`flex ${m.role === "user" ? "justify-end" : ""}`}>
                <div className={`min-w-0 max-w-[94%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words ${m.role === "user"
                  ? "bg-[var(--aria-text)] text-[var(--aria-bg)] rounded-tr-sm"
                  : "bg-rose-500/10 border border-rose-500/25 rounded-tl-sm"}`}>
                  {m.content}
                  <AttachmentList files={m.attachments} prefix={`nishu-message-${i}`} />
                  {m.role === "assistant" && (
                    <button onClick={() => (playing ? stopSpeak() : speak(m.content, key))} data-testid={`nishu-play-${i}`}
                      title={playing ? "Stop" : "Play voice clip"}
                      className={`mt-1.5 inline-flex items-center gap-1 text-[11px] transition-colors ${playing ? "text-rose-300" : "text-rose-400/80 hover:text-rose-300"}`}>
                      {playing ? <Square size={11} /> : <Volume2 size={11} />} {playing ? "Playing…" : "Play voice"}
                    </button>
                  )}
                </div>
              </div>
              );
            })}
            {thinking && (
              <div className="flex" data-testid="nishu-thinking">
                <div className="rounded-2xl rounded-tl-sm bg-rose-500/10 border border-rose-500/25 px-3.5 py-2.5">
                  <span className="inline-flex items-center gap-1"><i className="typing-dot" /><i className="typing-dot" /><i className="typing-dot" /></span>
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="p-2.5 border-t border-[var(--aria-border)]" data-testid="nishu-attachment-drop-zone"
            onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); if (!sending && !recording) attachments.upload(e.dataTransfer.files); }}>
            <div className="max-h-36 overflow-y-auto"><AttachmentDraft channel="nishu" attachments={attachments} disabled={sending} /></div>
            <div className="flex items-center gap-2">
            <button onClick={toggleMic} disabled={sending || historyLoading || attachments.uploading} data-testid="nishu-mic" title={recording ? "Stop & send" : "Talk to Nishu"}
              className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center border transition-colors ${recording ? "border-rose-500 text-rose-400 bg-rose-500/10 animate-pulse" : "border-[var(--aria-border)] text-[var(--aria-text-muted)] hover:text-rose-400 hover:border-rose-500/50"}`}>
              {recording ? <Square size={15} /> : <Mic size={15} />}
            </button>
            <input value={input} maxLength={2000} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(input); }}
              data-testid="nishu-input" placeholder="Talk to Nishu…"
              className="flex-1 min-w-0 bg-[var(--aria-bg)] border border-[var(--aria-border)] rounded-full px-3.5 py-2 text-sm outline-none focus:border-rose-500/60" />
            <button onClick={() => send(input)} disabled={sending || historyLoading || attachments.uploading || (!input.trim() && !attachments.selected.length)} data-testid="nishu-send"
              className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-gradient-to-br from-rose-500 to-pink-600 text-white disabled:opacity-40">
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            </button>
            </div>
            <div className="flex items-center gap-1 mt-1 text-rose-400"><AttachmentTools channel="nishu" attachments={attachments} disabled={sending || recording} />
              <span className="text-[10px] text-[var(--aria-text-muted)]" data-testid="nishu-attachment-limits">Private · 25 MB per file</span></div>
          </div>
        </div>
      )}
    </>
  );
}
